import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import ESignatureModal from "components/Lims/ESignatureModal"
import { limsSpecVersions, message } from "state_management"
import { SPEC_VERSION_STATES } from "state_management/lims/limsSpecVersions"

// SpecChangeControl (F7, SPEC-039) — the QC method spec is under reviewer→supervisor change
// control. Changing the spec content on this definition and saving creates a new DRAFT version
// SERVER-SIDE (the effective spec result-entry reads is untouched until the draft is approved).
// This section, shown only when editing an EXISTING definition with the LIMS module on, lists the
// spec's version history and offers the reviewer/supervisor e-sign actions on the pending version.
//
// It owns its own data: it fetches the versions for this definition on mount (capabilities ride on
// the member dump, so no extra fetch for canSign). A `draft` offers Reviewer-approve (capability
// "reviewer", 4-eyes ≠ the version creator); a `reviewer-approved` version offers Supervisor-approve
// (capability "supervisor", 4-eyes ≠ the reviewer signer). On supervisor approval the version goes
// effective and the prior effective version is superseded server-side.
export default function SpecChangeControl({ analysisDefinitionId }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const versions = useSelector((state) =>
    limsSpecVersions.selectVersionsForDefinition(state, analysisDefinitionId)
  )

  useEffect(() => {
    if (analysisDefinitionId) dispatch(limsSpecVersions.fetch(analysisDefinitionId))
  }, [dispatch, analysisDefinitionId])

  if (!analysisDefinitionId) return null

  return (
    <div className="LimsSpecChangeControl">
      <p className="LimsSpecChangeControl-help">{t("limsSpecVersionHelp")}</p>
      {versions.length === 0 ? (
        <p className="LimsSpecChangeControl-empty">{t("limsSpecVersionNone")}</p>
      ) : (
        <Table size="sm" className="LimsSpecChangeControl-table">
          <thead>
            <tr>
              <th>{t("limsForskriftVersion")}</th>
              <th>{t("limsForskriftStatus")}</th>
              <th>{t("limsForskriftReviewedBy")}</th>
              <th>{t("limsForskriftSupervisorBy")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((version) => (
              <SpecVersionRow key={version.id} version={version} />
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

function SpecVersionRow({ version }) {
  const { t } = useT("common")
  const members = useSelector((state) => state.members)
  const initials = (sig) =>
    (sig && sig.memberId && members[sig.memberId] && members[sig.memberId].initials) || "—"

  return (
    <tr>
      <td>v{version.versionNumber}</td>
      <td>
        <span className={"LimsForskrifter-status " + statusClass(version.state)}>
          {t(statusLabelKey(version.state))}
        </span>
      </td>
      <td>{version.reviewerSignature ? initials(version.reviewerSignature) : "—"}</td>
      <td>{version.supervisorSignature ? initials(version.supervisorSignature) : "—"}</td>
      <td className="LimsSpecChangeControl-actions">
        <SpecVersionApproval version={version} />
      </td>
    </tr>
  )
}

function SpecVersionApproval({ version }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [signing, setSigning] = useState(null) // "reviewer" | "supervisor" | null
  const [saving, setSaving] = useState(false)

  const handleReviewerApprove = (signature) => {
    setSaving(true)
    return dispatch(limsSpecVersions.reviewerApprove(version.id, signature))
      .then(() => {
        setSigning(null)
        dispatch(message.success(t("limsSpecVersionReviewerApproved", { n: version.versionNumber })))
      })
      .finally(() => setSaving(false))
  }

  const handleSupervisorApprove = (signature) => {
    setSaving(true)
    return dispatch(limsSpecVersions.supervisorApprove(version.id, signature))
      .then(() => {
        setSigning(null)
        dispatch(message.success(t("limsSpecVersionSupervisorApproved", { n: version.versionNumber })))
      })
      .finally(() => setSaving(false))
  }

  // 4-eyes exclusions: the reviewer cannot be the version creator; the supervisor cannot be the
  // reviewer signer (the server enforces this too; we pre-exclude for a clearer signer picker).
  const creatorId = version.createdBy
  const reviewerId = version.reviewerSignature && version.reviewerSignature.memberId

  return (
    <>
      {version.state === SPEC_VERSION_STATES.DRAFT && (
        <Button variant="primary" size="sm" disabled={saving} onClick={() => setSigning("reviewer")}>
          {t("limsForskriftReviewerApprove")}
        </Button>
      )}
      {version.state === SPEC_VERSION_STATES.REVIEWER_APPROVED && (
        <Button variant="success" size="sm" disabled={saving} onClick={() => setSigning("supervisor")}>
          {t("limsForskriftSupervisorApprove")}
        </Button>
      )}

      {signing === "reviewer" && (
        <ESignatureModal
          title={t("limsSpecVersionReviewerApproveTitle")}
          requiredCapability="reviewer"
          excludedMemberIds={creatorId ? [creatorId] : []}
          onSign={handleReviewerApprove}
          onCancel={() => setSigning(null)}
        />
      )}
      {signing === "supervisor" && (
        <ESignatureModal
          title={t("limsSpecVersionSupervisorApproveTitle")}
          requiredCapability="supervisor"
          excludedMemberIds={reviewerId ? [reviewerId] : []}
          onSign={handleSupervisorApprove}
          onCancel={() => setSigning(null)}
        />
      )}
    </>
  )
}

function statusLabelKey(state) {
  if (state === SPEC_VERSION_STATES.REVIEWER_APPROVED) return "limsForskriftStatusReviewerApproved"
  if (state === SPEC_VERSION_STATES.EFFECTIVE) return "limsForskriftStatusEffective"
  if (state === SPEC_VERSION_STATES.SUPERSEDED) return "limsForskriftStatusSuperseded"
  return "limsForskriftStatusDraft"
}

function statusClass(state) {
  if (state === SPEC_VERSION_STATES.EFFECTIVE) return "is-effective"
  if (state === SPEC_VERSION_STATES.REVIEWER_APPROVED) return "is-reviewer"
  if (state === SPEC_VERSION_STATES.SUPERSEDED) return "is-superseded"
  return "is-draft"
}
